// src/store/modules/auth.js - Final integrated version
import AuthenticationService from '../../services/authentication/AuthenticationService'
import UserService from '../../services/users/UserService'
import AnalyticsService from '../../services/analytics/AnalyticsService'

export default {
  namespaced: true,
  state: {
    user: AuthenticationService.getUserData(),
    token: AuthenticationService.getJwt(),
    loading: false,
    error: null,
    // Impersonation state
    isImpersonating: AuthenticationService.isImpersonating(),
    originalUser: null,
    impersonatedUser: null
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user
      // Update AuthenticationService cache
      if (user) {
        AuthenticationService.setUserData(user)
      }
    },

    SET_TOKEN(state, token) {
      state.token = token
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_IMPERSONATION(state, { isImpersonating, originalUser, impersonatedUser }) {
      state.isImpersonating = isImpersonating
      state.originalUser = originalUser
      state.impersonatedUser = impersonatedUser
    },

    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      state.loading = false
      state.error = null
      state.isImpersonating = false
      state.originalUser = null
      state.impersonatedUser = null
      AuthenticationService.clearAuthData()
    }
  },

  actions: {
    async login({ commit }, credentials) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const result = await AuthenticationService.login(credentials)

        if (result.success) {
          commit('SET_USER', AuthenticationService.getUserData())
          commit('SET_TOKEN', AuthenticationService.getJwt())
          const user = AuthenticationService.getUserData()
          AnalyticsService.trackEvent('login', { method: 'password' })
          AnalyticsService.setUserProperties({ role: user?.roles?.[0]?.name || 'unknown' })
          return result
        } else {
          commit('SET_ERROR', result.message)
          throw new Error(result.message)
        }
      } catch (error) {
        const errorMessage = error.message || 'Login failed'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async register({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const result = await AuthenticationService.register(userData)

        if (result.success) {
          commit('SET_USER', AuthenticationService.getUserData())
          commit('SET_TOKEN', AuthenticationService.getJwt())
          AnalyticsService.trackEvent('sign_up', { method: 'password' })
          return result
        } else {
          commit('SET_ERROR', result.message)
          throw new Error(result.message)
        }
      } catch (error) {
        const errorMessage = error.message || 'Registration failed'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async logout({ commit }) {
      try {
        commit('SET_LOADING', true)
        await AuthenticationService.logout()
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        commit('CLEAR_AUTH')
        AnalyticsService.trackEvent('logout')
      }
    },

    async forgotPassword({ commit }, email) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const result = await AuthenticationService.forgotPassword(email)

        if (!result.success) {
          commit('SET_ERROR', result.message)
          throw new Error(result.message)
        }

        AnalyticsService.trackEvent('password_reset_request')
        return result
      } catch (error) {
        const errorMessage = error.message || 'Failed to send reset link'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async resetPassword({ commit }, resetData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const result = await AuthenticationService.resetPassword(resetData)

        if (!result.success) {
          commit('SET_ERROR', result.message)
          throw new Error(result.message)
        }

        AnalyticsService.trackEvent('password_reset_complete')
        return result
      } catch (error) {
        const errorMessage = error.message || 'Failed to reset password'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async changePassword({ commit }, passwordData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const result = await AuthenticationService.changePassword(passwordData)

        if (!result.success) {
          commit('SET_ERROR', result.message)
          throw new Error(result.message)
        }

        return result
      } catch (error) {
        const errorMessage = error.message || 'Failed to change password'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateProfile({ commit }, profileData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const result = await AuthenticationService.updateProfile(profileData)

        if (result.success) {
          commit('SET_USER', result.data)
          return result
        } else {
          commit('SET_ERROR', result.message)
          throw new Error(result.message)
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to update profile'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async refreshToken({ commit }) {
      try {
        const result = await AuthenticationService.refreshToken()

        if (result.success) {
          commit('SET_TOKEN', AuthenticationService.getJwt())
          return result
        } else {
          commit('CLEAR_AUTH')
          throw new Error(result.message)
        }
      } catch (error) {
        commit('CLEAR_AUTH')
        throw error
      }
    },

    // NEW: Fetch current user - handles both regular and impersonation
    async fetchUser({ commit }) {
      try {
        const response = await UserService.getCurrentAuthenticatedUser()

        let userData = null
        let isImpersonating = false
        let originalUser = null

        // Handle different response structures
        if (response?.status === 'success' && response?.data) {
          userData = response.data.user
          isImpersonating = response.data.is_impersonating || false
          originalUser = response.data.original_user || null
        } else if (response?.data?.user) {
          userData = response.data.user
          isImpersonating = response.data.is_impersonating || false
          originalUser = response.data.original_user || null
        }

        if (userData) {
          commit('SET_USER', userData)

          if (isImpersonating) {
            commit('SET_IMPERSONATION', {
              isImpersonating: true,
              originalUser: originalUser,
              impersonatedUser: userData
            })
          } else {
            commit('SET_IMPERSONATION', {
              isImpersonating: false,
              originalUser: null,
              impersonatedUser: null
            })
          }

          return userData
        } else {
          throw new Error('No user data in response')
        }
      } catch (error) {
        console.error('Vuex: Error fetching user:', error)

        // Only clear auth on 401 errors
        if (error?.response?.status === 401) {
          commit('CLEAR_AUTH')
        }

        throw error
      }
    },

    async checkAuthStatus({ commit, dispatch }) {
      const isAuthenticated = AuthenticationService.isAuthenticated()

      if (isAuthenticated) {
        // Check if we're impersonating
        const impersonationStatus = AuthenticationService.getImpersonationStatus()

        if (impersonationStatus.isImpersonating) {
          try {
            await dispatch('fetchUser')
          } catch (error) {
            console.error('Vuex: Error fetching impersonated user:', error)
            commit('CLEAR_AUTH')
            return false
          }
        } else {
          commit('SET_USER', AuthenticationService.getUserData())
          commit('SET_TOKEN', AuthenticationService.getJwt())
        }

        // Check if token needs refresh (skip for impersonation)
        if (!impersonationStatus.isImpersonating) {
          try {
            await AuthenticationService.checkAndRefreshToken()
          } catch (error) {
            console.error('Vuex: Token refresh error:', error)
          }
        }
      } else {
        commit('CLEAR_AUTH')
      }

      return isAuthenticated
    },

    clearError({ commit }) {
      commit('SET_ERROR', null)
    }
  },

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    user: (state) => state.user,
    token: (state) => state.token,
    loading: (state) => state.loading,
    error: (state) => state.error,
    userId: (state) => state.user?.id,
    userEmail: (state) => state.user?.email,
    userName: (state) => state.user?.name,
    userRole: (state) => state.user?.roles?.[0]?.name,
    userPermissions: (state) => state.user?.roles || [],
    isEmailVerified: (state) => !!state.user?.email_verified_at,
    // Impersonation getters
    isImpersonating: (state) => state.isImpersonating,
    originalUser: (state) => state.originalUser,
    impersonatedUser: (state) => state.impersonatedUser,
    effectiveUser: (state) => state.isImpersonating ? state.impersonatedUser : state.user
  }
}
